import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import type { GridPaginationModel } from "@mui/x-data-grid";
import type { UseContasFinanceirasFinanceiroResult } from "../hooks/useContasFinanceirasFinanceiro";
import type { UseExtratoFinanceiroResult } from "../hooks/useExtratoFinanceiro";
import type { ExtratoFinanceiroFiltros as ExtratoFiltrosState } from "../types/extrato-financeiro.types";
import FinanceiroErrorState from "./FinanceiroErrorState";
import FinanceiroSection from "./FinanceiroSection";
import ExtratoFinanceiroFiltros from "./ExtratoFinanceiroFiltros";
import ExtratoFinanceiroResumoCards from "./ExtratoFinanceiroResumoCards";
import ExtratoFinanceiroTabela from "./ExtratoFinanceiroTabela";

function CardsSkeleton() {
  return (
    <Grid container spacing={2}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rounded" height={112} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export type ExtratoFinanceiroSectionProps = {
  extrato: UseExtratoFinanceiroResult;
  contasFinanceiras: UseContasFinanceirasFinanceiroResult;
  /** Sem `FinanceiroSection` — página dedicada com `LojaPageContainer`. */
  embedded?: boolean;
};

export default function ExtratoFinanceiroSection({
  extrato,
  contasFinanceiras,
  embedded,
}: ExtratoFinanceiroSectionProps) {
  const [periodoResetTick, setPeriodoResetTick] = useState(0);

  const handleLimpar = useCallback(() => {
    extrato.limparFiltros();
    setPeriodoResetTick((n) => n + 1);
  }, [extrato]);

  const handlePaginationModelChange = useCallback(
    (m: GridPaginationModel) => {
      extrato.setPagination(m.page + 1, m.pageSize);
    },
    [extrato]
  );

  const setFiltrosWrapper: Dispatch<SetStateAction<ExtratoFiltrosState>> =
    useCallback(
      (action) => {
        extrato.setFiltros(action);
      },
      [extrato]
    );

  const { loading, error, isEmpty, data } = extrato;

  const corpo = (
    <Stack spacing={2}>
      {error ? (
        <FinanceiroErrorState message={error} onRetry={extrato.refetch} />
      ) : null}

      {!error && loading && !data ? <CardsSkeleton /> : null}

      <ExtratoFinanceiroFiltros
        filtros={extrato.filtros}
        setFiltros={setFiltrosWrapper}
        onAplicar={extrato.aplicarFiltros}
        onLimpar={handleLimpar}
        loading={loading}
        contasFinanceiras={contasFinanceiras.data}
        contasFinanceirasIndisponivel={Boolean(contasFinanceiras.error)}
        resetPeriodoPresetTick={periodoResetTick}
      />
      {!error && data ? (
        <>
          <ExtratoFinanceiroResumoCards
            resumo={data.resumo}
            loading={loading}
          />
          {!loading && isEmpty ? (
            <Alert severity="info">
              Nenhuma movimentação financeira encontrada para os filtros
              selecionados.
            </Alert>
          ) : null}
          {!isEmpty ? (
            <Box>
              <ExtratoFinanceiroTabela
                linhas={data.itens}
                total={data.total}
                page={extrato.page}
                limit={extrato.limit}
                loading={loading}
                onPaginationModelChange={handlePaginationModelChange}
              />
            </Box>
          ) : null}
        </>
      ) : null}
    </Stack>
  );

  if (embedded) return corpo;

  return (
    <FinanceiroSection
      titulo="Extrato Financeiro"
      subtitulo="Acompanhe entradas, saídas, taxas, transferências e ajustes financeiros."
    >
      {corpo}
    </FinanceiroSection>
  );
}
